import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getFavoriteArtists, getOnboardingProfile } from '../../utils/backendApi';
import { getApplaussAuth, isOnboardingDeclined } from '../../utils/authManager';
import { isInitialValidationComplete } from '../../utils/authValidator';
import SplashScreen from '../../components/ui/SplashScreen';
import { getPreferredCity } from '../../hooks/useAvailableCities';
import { buildRecommendationEntry } from '../../utils/recommendationFilters';
import {
  buildPreferencesFromOnboarding,
  getNextIncompleteOnboardingStep,
  isCompletedValidOnboarding,
  persistOnboardingPreferences,
} from '../../utils/onboardingProgress';

const HomeRedirect = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);

  // Wait for auth validation to complete before proceeding
  useEffect(() => {
    // Check if there are existing auth tokens that need validation
    const auth = getApplaussAuth();
    const hasTokens = auth && auth.access_token;

    if (!hasTokens) {
      // No tokens, can proceed immediately
      console.log('🏠 HomeRedirect: No tokens found, proceeding immediately');
      setAuthChecked(true);
      return;
    }

    // Check if initial validation already completed (prevents race condition)
    if (isInitialValidationComplete()) {
      console.log('🏠 HomeRedirect: Initial validation already completed, proceeding');
      setAuthChecked(true);
      return;
    }

    // Has tokens and validation not complete - wait for validation to complete
    console.log('🏠 HomeRedirect: Tokens found, waiting for validation...');
    let validationComplete = false;

    const handleAuthEvent = () => {
      if (!validationComplete) {
        console.log('🏠 HomeRedirect: Auth validation complete via event');
        validationComplete = true;
        setAuthChecked(true);
      }
    };

    // Listen for auth events that indicate validation is complete
    window.addEventListener('authChanged', handleAuthEvent);
    window.addEventListener('sessionRefreshed', handleAuthEvent);

    // Reduced timeout since we now have synchronous check (1 second instead of 2)
    const maxWaitTimer = setTimeout(() => {
      if (!validationComplete) {
        console.log('🏠 HomeRedirect: Max wait time reached, proceeding');
        validationComplete = true;
        setAuthChecked(true);
      }
    }, 1000);

    return () => {
      window.removeEventListener('authChanged', handleAuthEvent);
      window.removeEventListener('sessionRefreshed', handleAuthEvent);
      clearTimeout(maxWaitTimer);
    };
  }, []);

  useEffect(() => {
    // Don't proceed until auth has been checked
    if (!authChecked) {
      console.log('🏠 HomeRedirect: Waiting for auth check...');
      return;
    }

    const loadAndRedirect = async () => {
      console.log('🏠 HomeRedirect: Starting redirect logic', { isAuthenticated });

      const navigateWithPreferences = async (preferences) => {
        // Within a tab, "home" means the feed you were last looking at — city, filters
        // and mode — and only falls back to saved preferences on a cold entry.
        //
        // The city was already right: `getPreferredCity` reads `lastSelectedCity` before
        // `preferences.city`. What the logo lost was everything else. `preferences.filters`
        // is the last set *applied*, not the set on screen, and it has never heard of the
        // mode — so tapping the logo from the map dropped the reader on a list
        // with a filter set they may have moved on from (#518).
        const { options: filters, params: urlParams, city: rememberedCity } =
          buildRecommendationEntry(preferences);
        const city = rememberedCity || getPreferredCity(preferences);

        if (!city) {
          navigate('/event-recommendations', { replace: true });
          return;
        }

        urlParams.set('city', city);
        const recommendationsPath = `/event-recommendations?${urlParams.toString()}`;

        // Hand over the city and filters, never the events. Fetching them here meant the
        // whole request happened behind the splash — a branded blank screen with nothing
        // on it saying anything was coming — and the recommendations page already has a
        // view for exactly that wait: skeleton cards under "Still choosing events for
        // you", the same one a filter change shows. Leaving `recommendations` out is what
        // makes the page's `hasStateData` false, so it fetches and renders that view.
        //
        // The other two branches here always did this; only the authenticated happy path
        // held on, which is why the wait was invisible in the code and obvious on screen.
        console.log('🏠 HomeRedirect: Navigating to recommendations, the page fetches them', { city });
        navigate(recommendationsPath, {
          replace: true,
          state: {
            city,
            filters
          }
        });
      };

      if (isAuthenticated) {
        try {
          const [profileResponse, artistsResponse] = await Promise.all([
            getOnboardingProfile(),
            getFavoriteArtists(),
          ]);
          const profile = profileResponse?.profile;
          const artists = artistsResponse?.artists || [];

          if (isCompletedValidOnboarding(profile)) {
            const preferences = buildPreferencesFromOnboarding(profile, artists);
            // Keep the user's live filter edits saved on the recommendations page;
            // only refresh the non-filter fields from the backend onboarding profile.
            const persisted = persistOnboardingPreferences(preferences, { preserveExistingFilters: true });
            await navigateWithPreferences(persisted || preferences);
            return;
          }

          if (getNextIncompleteOnboardingStep(profile)) {
            console.log('🏠 HomeRedirect: Backend onboarding incomplete, going to onboarding');
            navigate('/onboarding', { replace: true });
            return;
          }
        } catch (error) {
          console.warn('🏠 HomeRedirect: Backend onboarding profile unavailable', error.response?.data || error.message);
        }
      }

      // Check if onboarding is completed
      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      const declinedPersonalisation = isOnboardingDeclined();
      const userPreferences = localStorage.getItem('user_preferences');

      console.log('🏠 HomeRedirect: Checking preferences', {
        onboardingCompleted: !!onboardingCompleted,
        declinedPersonalisation,
        hasPreferences: !!userPreferences
      });

      // Onboarding is skipped when it was completed OR declined at the consent
      // gate — a decliner must land on the listing, not be re-prompted forever.
      // Completion still wins over a stale marker: someone who declined and
      // later came back through the banner is no longer a decliner (granting
      // consent also clears the marker, so this is belt and braces).

      if ((!onboardingCompleted && !declinedPersonalisation) || !userPreferences) {
        // First time or no preferences saved
        console.log('🏠 HomeRedirect: No onboarding/preferences, going to onboarding');
        navigate('/onboarding', { replace: true });
        return;
      }

      // Has preferences, go to recommendations
      try {
        const preferences = JSON.parse(userPreferences);
        await navigateWithPreferences(preferences);
      } catch (error) {
        console.error('Failed to parse user preferences:', error);
        // If preferences are corrupted, go to onboarding
        navigate('/onboarding', { replace: true });
      }
    };

    loadAndRedirect();
  }, [navigate, isAuthenticated, authChecked]);

  // Show loading while redirecting
  return <SplashScreen captureRect />;
};

export default HomeRedirect;
